import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { EMPTY, of } from 'rxjs';
import { catchError, map, mergeMap, startWith } from 'rxjs/operators';

import { OktaUserService } from '../services/okta-user';
import { OktaUsersActions } from './okta-users.actions';
import { isFresh, oktaUsersFeature, pageKey } from './okta-users.reducer';

/**
 * Fetches only when the cached entry is missing or older than the TTL. Pages
 * are fetched one at a time on request; nothing here follows nextCursor.
 */
@Injectable()
export class OktaUsersEffects {
  private readonly actions$ = inject(Actions);

  private readonly store = inject(Store);

  private readonly oktaUserService = inject(OktaUserService);

  loadPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OktaUsersActions.loadPage),
      concatLatestFrom(() => [
        this.store.select(oktaUsersFeature.selectPages),
        this.store.select(oktaUsersFeature.selectPageStatus),
      ]),
      mergeMap(([{ after }, pages, status]) => {
        const key = pageKey(after);
        if (isFresh(pages[key], Date.now()) || status[key]?.loading) {
          return EMPTY;
        }
        return this.oktaUserService.getUsersPage(after).pipe(
          map((page) =>
            OktaUsersActions.loadPageSuccess({
              after,
              page,
              fetchedAt: Date.now(),
            }),
          ),
          catchError((err) =>
            of(
              OktaUsersActions.loadPageFailure({
                after,
                error:
                  err?.message || 'Failed to load users. Please try again.',
              }),
            ),
          ),
          startWith(OktaUsersActions.loadPageStarted({ after })),
        );
      }),
    ),
  );

  loadUserMfa$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OktaUsersActions.loadUserMfa),
      concatLatestFrom(() => [
        this.store.select(oktaUsersFeature.selectMfaByUserId),
        this.store.select(oktaUsersFeature.selectMfaStatus),
      ]),
      mergeMap(([{ userId, login }, mfaByUserId, status]) => {
        if (
          isFresh(mfaByUserId[userId], Date.now()) ||
          status[userId]?.loading
        ) {
          return EMPTY;
        }
        return this.oktaUserService.getUserFactors(login).pipe(
          map((mfa) =>
            OktaUsersActions.loadUserMfaSuccess({
              userId,
              mfa,
              fetchedAt: Date.now(),
            }),
          ),
          catchError((err) =>
            of(
              OktaUsersActions.loadUserMfaFailure({
                userId,
                error: err?.message || 'Failed to load MFA.',
              }),
            ),
          ),
          startWith(OktaUsersActions.loadUserMfaStarted({ userId })),
        );
      }),
    ),
  );
}
